/**
 * Theme preference. Client state with no business knowledge, so it lives in `shared/`.
 *
 * Three states, not two: `system` is the default and follows the OS, which matters because a
 * shop is bright at 10am and dim at 7pm and the phone already knows that.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'pos.theme';

function read(): ThemeChoice {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Writes the attribute the tokens key off. Called from the slice rather than an effect: this is
 * the same write the inline script in `index.html` does before React mounts, and keeping both in
 * one function is what stops them disagreeing.
 */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;

  if (choice === 'system') delete root.dataset.theme;
  else root.dataset.theme = choice;

  try {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* storage disabled; the choice lasts for this tab only */
  }
}

const themeSlice = createSlice({
  name: 'theme',
  initialState: { choice: read() },
  reducers: {
    themeChosen(state, action: PayloadAction<ThemeChoice>) {
      state.choice = action.payload;
      applyTheme(action.payload);
    },
  },
});

export const { themeChosen } = themeSlice.actions;
export const themeReducer = themeSlice.reducer;

type WithTheme = { readonly theme: { readonly choice: ThemeChoice } };

export const selectTheme = (state: WithTheme): ThemeChoice => state.theme.choice;
